import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FixedSizeList, type ListChildComponentProps } from 'react-window';
import { Box, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';

import type { WorkPositionTreeNode } from '../dialog/employee/hooks/AssignPosition/useAssignPositionForm';
import { type FlatTreeItem, useVirtualTreeState } from './hooks/useVirtualTreeState';

const ITEM_HEIGHT = 48;

export interface VirtualTreeApiRef {
    expandAll: () => void;
    collapseAll: () => void;
    setItemExpansion: (itemId: string, isExpanded: boolean) => void;
    /** Expands ancestors and scrolls to the item after the next render. */
    focusItem: (itemId: string) => void;
    /** Searches the full tree regardless of expansion state. */
    getItem: (itemId: string) => FlatTreeItem | undefined;
    scrollToItem: (itemId: string) => void;
}

export interface VirtualTreeProps {
    subcompanyTree: { subcompany: Subcompany; rootAreas: import('../dialog/employee/hooks/AssignPosition/useAssignPositionForm').AreaTreeNode[] }[];
    onSelectWorkPosition: (wp: WorkPositionTreeNode, itemId: string) => void;
    /** Path-based ids of ya-asignados (formato: "sub-1/area-5/.../wp-12") */
    assignedWorkPositionIds: Set<string>;
    /** Path-based id de la fila actualmente seleccionada (pendiente de confirmar). */
    selectedItemId?: string | null;
    height?: number;
    /** IDs a expandir al montar; si se omite se expanden las subcompañías raíz */
    initialExpandedIds?: Set<string>;
}

interface RowData {
    items: FlatTreeItem[];
    expandedIds: Set<string>;
    toggleExpansion: (id: string) => void;
    onSelect: (wp: WorkPositionTreeNode, itemId: string) => void;
    assignedWorkPositionIds: Set<string>;
    selectedItemId?: string | null;
    highlightQuery: string;
}

// Wraps matching text in a <mark> element for search highlight.
function HighlightedText({ text, query }: { text: string; query: string }) {
    if (!query) return <>{text}</>;
    const lower = text.toLowerCase();
    const idx = lower.indexOf(query.toLowerCase());
    if (idx === -1) return <>{text}</>;
    return (
        <>
            {text.slice(0, idx)}
            <Box component="mark" sx={{ bgcolor: '#facc15', color: '#1a1a1a', borderRadius: 0.25, px: 0.25 }}>
                {text.slice(idx, idx + query.length)}
            </Box>
            {text.slice(idx + query.length)}
        </>
    );
}

// Row renderer for FixedSizeList — must be outside the parent component to avoid recreation.
const TreeRow = React.memo<ListChildComponentProps<RowData>>(({ index, style, data }) => {
    const { items, expandedIds, toggleExpansion, onSelect, assignedWorkPositionIds, selectedItemId, highlightQuery } = data;
    const item = items[index];
    if (!item) return null;

    const isExpanded = expandedIds.has(item.id);
    const isWP = item.type === 'work_position';
    // El chequeo usa el path completo del item para no marcar duplicados con el mismo work_position_id en otras ramas.
    const isAssigned = isWP ? assignedWorkPositionIds.has(item.id) : false;
    const isSelected = isWP && selectedItemId === item.id;
    const isClickable = isWP || item.hasChildren;

    const handleClick = () => {
        if (isWP && item.workPosition) onSelect(item.workPosition, item.id);
        else if (item.hasChildren) toggleExpansion(item.id);
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            style={style}
            onClick={handleClick}
            sx={{
                pl: `${8 + item.depth * 20}px`,
                pr: 1,
                cursor: isClickable ? 'pointer' : 'default',
                borderRadius: 1,
                transition: 'background-color 0.1s, box-shadow 0.1s',
                bgcolor: isSelected ? 'action.selected' : undefined,
                boxShadow: isSelected ? (theme) => `inset 3px 0 0 ${theme.palette.primary.main}` : undefined,
                '&:hover': isClickable ? { bgcolor: isSelected ? 'action.selected' : 'action.hover' } : undefined,
                userSelect: 'none',
            }}
        >
            {/* Chevron as visual-only indicator — click is handled by the whole row */}
            <Box sx={{ width: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.hasChildren && <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'none' }} />}
            </Box>

            {item.type === 'subcompany' && <BusinessIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.75, flexShrink: 0 }} />}
            {item.type === 'area' &&
                (isExpanded ? (
                    <FolderOpenIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.75, flexShrink: 0 }} />
                ) : (
                    <FolderIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.75, flexShrink: 0 }} />
                ))}
            {isWP && <WorkOutlineIcon sx={{ fontSize: 16, color: isAssigned ? 'success.main' : 'primary.light', mr: 0.75, flexShrink: 0 }} />}

            <Typography
                variant="body2"
                fontWeight={item.type === 'subcompany' ? 600 : item.type === 'area' ? 500 : 400}
                color={item.type === 'subcompany' ? 'primary' : isWP ? (isAssigned ? 'success.main' : 'text.primary') : 'text.secondary'}
                noWrap
                sx={{ flex: 1, minWidth: 0 }}
            >
                <HighlightedText text={item.label} query={highlightQuery} />
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, flexShrink: 0 }}>
                {item.type === 'subcompany' ? '· Sub compañía' : item.type === 'area' ? '· Área' : '· Puesto'}
            </Typography>

            {isAssigned && <Chip size="small" color="success" variant="outlined" label="Asignado" sx={{ height: 18, ml: 1, flexShrink: 0, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />}
        </Stack>
    );
});

export const VirtualTree = forwardRef<VirtualTreeApiRef, VirtualTreeProps>(function VirtualTree(
    { subcompanyTree, onSelectWorkPosition, assignedWorkPositionIds, selectedItemId, height = 400, initialExpandedIds },
    ref,
) {
    const { t } = useTranslation();
    const listRef = useRef<FixedSizeList>(null);
    // Stores the target item ID to scroll to after flatVisibleItems updates (from expandAncestors).
    const pendingFocusRef = useRef<string | null>(null);

    const [rawQuery, setRawQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useEffect(() => {
        const id = setTimeout(() => setDebouncedQuery(rawQuery), 200);
        return () => clearTimeout(id);
    }, [rawQuery]);

    const { flatVisibleItems, expandedIds, allItemsMap, toggleExpansion, expandAll, collapseAll, setItemExpansion, expandAncestors } = useVirtualTreeState(subcompanyTree, initialExpandedIds);

    const isSearching = debouncedQuery.trim().length >= 2;

    // When searching: find WPs whose label matches OR that belong to a matching ancestor node.
    const searchResults = useMemo((): FlatTreeItem[] => {
        if (!isSearching) return [];
        const q = debouncedQuery.trim().toLowerCase();

        // Step 1: which item labels match?
        const labelMatches = new Set<string>();
        for (const [id, item] of allItemsMap) {
            if (item.label.toLowerCase().includes(q)) labelMatches.add(id);
        }

        // Step 2: collect relevant work positions —
        //   a WP is relevant if its own label matches OR any ancestor's label matches.
        const relevantWPs = new Set<string>();
        for (const [id, item] of allItemsMap) {
            if (item.type !== 'work_position') continue;
            if (labelMatches.has(id)) {
                relevantWPs.add(id);
                continue;
            }
            const parts = id.split('/');
            for (let i = 1; i < parts.length; i++) {
                if (labelMatches.has(parts.slice(0, i).join('/'))) {
                    relevantWPs.add(id);
                    break;
                }
            }
        }

        // Step 3: collect ancestors of all relevant WPs to show tree context.
        const ancestorIds = new Set<string>();
        for (const wpId of relevantWPs) {
            const parts = wpId.split('/');
            for (let i = 1; i < parts.length; i++) ancestorIds.add(parts.slice(0, i).join('/'));
        }

        const visible = new Set([...relevantWPs, ...ancestorIds]);
        // allItemsMap preserves insertion order (tree traversal order).
        return [...allItemsMap.values()].filter((item) => visible.has(item.id));
    }, [allItemsMap, debouncedQuery, isSearching]);

    const displayItems = isSearching ? searchResults : flatVisibleItems;

    // Process deferred focusItem once flatVisibleItems updates after expandAncestors.
    useEffect(() => {
        if (!pendingFocusRef.current) return;
        const id = pendingFocusRef.current;
        pendingFocusRef.current = null;
        const index = flatVisibleItems.findIndex((item) => item.id === id);
        if (index >= 0) listRef.current?.scrollToItem(index, 'smart');
    }, [flatVisibleItems]);

    const scrollToItemById = useCallback(
        (id: string) => {
            const index = displayItems.findIndex((item) => item.id === id);
            if (index >= 0) listRef.current?.scrollToItem(index, 'smart');
        },
        [displayItems],
    );

    useImperativeHandle(ref, () => ({
        expandAll,
        collapseAll,
        setItemExpansion,
        focusItem: (id: string) => {
            expandAncestors(id);
            pendingFocusRef.current = id;
        },
        getItem: (id: string) => allItemsMap.get(id),
        scrollToItem: scrollToItemById,
    }));

    const rowData = useMemo<RowData>(
        () => ({
            items: displayItems,
            expandedIds,
            toggleExpansion,
            onSelect: onSelectWorkPosition,
            assignedWorkPositionIds,
            selectedItemId,
            highlightQuery: isSearching ? debouncedQuery.trim() : '',
        }),
        [displayItems, expandedIds, toggleExpansion, onSelectWorkPosition, assignedWorkPositionIds, selectedItemId, isSearching, debouncedQuery],
    );

    return (
        <Stack spacing={1}>
            <TextField
                size="small"
                fullWidth
                placeholder={t('timekeeper.employees.dialog.assign_position.search_placeholder', 'Buscar subcompañía, área o puesto...')}
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                        endAdornment: rawQuery ? (
                            <InputAdornment position="end">
                                <Tooltip title={t('general.actions.clear', 'Limpiar')}>
                                    <IconButton size="small" onClick={() => setRawQuery('')}>
                                        <ClearIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    },
                }}
            />

            {isSearching && searchResults.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {t('timekeeper.employees.dialog.assign_position.no_results', 'Sin resultados para "{{query}}"', { query: debouncedQuery.trim() })}
                    </Typography>
                </Box>
            ) : (
                <FixedSizeList ref={listRef} height={height} itemCount={displayItems.length} itemSize={ITEM_HEIGHT} itemData={rowData} width="100%" style={{ outline: 'none' }}>
                    {TreeRow}
                </FixedSizeList>
            )}
        </Stack>
    );
});
